import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { adminAPI } from '@/services/api';

const TOKEN_KEY = 'cardx_admin_token';

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);

  const applySession = useCallback((sessionToken) => {
    sessionStorage.setItem(TOKEN_KEY, sessionToken);
    setToken(sessionToken);
    setAuthenticated(true);
    setError(null);
  }, []);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAuthenticated(false);
  }, []);

  const login = useCallback(
    async (email, password) => {
      if (!(email || '').trim() || !(password || '')) {
        setError('Enter your email and password.');
        return false;
      }
      setVerifying(true);
      setError(null);
      try {
        const data = await adminAPI.login(email.trim(), password);
        const sessionToken = data?.token;
        if (!sessionToken) {
          setError('Login succeeded but no session token was returned.');
          return false;
        }
        applySession(sessionToken);
        return true;
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.detail || err?.message || 'Authentication failed';
        setError(typeof detail === 'string' ? detail : 'Authentication failed');
        clearSession();
        return false;
      } finally {
        setVerifying(false);
      }
    },
    [applySession, clearSession]
  );

  const verifySession = useCallback(
    async (existingToken) => {
      const trimmed = (existingToken || '').trim();
      if (!trimmed) {
        clearSession();
        return false;
      }
      setVerifying(true);
      setError(null);
      try {
        await adminAPI.getOverview(trimmed);
        applySession(trimmed);
        return true;
      } catch (err) {
        clearSession();
        return false;
      } finally {
        setVerifying(false);
      }
    },
    [applySession, clearSession]
  );

  const logout = useCallback(() => {
    clearSession();
    setError(null);
  }, [clearSession]);

  const value = useMemo(
    () => ({
      token,
      authenticated,
      verifying,
      error,
      setError,
      login,
      verifySession,
      logout,
      setAuthenticated,
    }),
    [token, authenticated, verifying, error, login, verifySession, logout]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return ctx;
}

export { TOKEN_KEY };
